import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { ReportType } from "@shared/types";
import { formatAud } from "@shared/types";
import { PLANT_SPECIES_ID, isPlantSpeciesIdSlug } from "@shared/plantSpeciesId";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

function regionOf(slug: string): "au" | "sg" {
  const s = slug.toLowerCase();
  if (
    s.startsWith("building-") ||
    s.startsWith("energy-") ||
    s.startsWith("acmv-") ||
    s.startsWith("chiller-") ||
    s.startsWith("electrical-") ||
    s.startsWith("plumbing-") ||
    s.startsWith("bms-") ||
    s.startsWith("green-mark") ||
    s.startsWith("retro-") ||
    s.startsWith("ieq-") ||
    s.startsWith("mv-") ||
    s.startsWith("solar-") ||
    s.startsWith("mep-") ||
    s.startsWith("ashrae-")
  ) {
    return "sg";
  }
  return "au";
}

export function ReportsPage() {
  const [types, setTypes] = useState<ReportType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const typeFilter = params.get("type") ?? "";
  const regionFilter = (params.get("region") as "au" | "sg" | "") || "";

  useEffect(() => {
    const previous = document.title;
    document.title = "Reports you can have this week from A$1,250 | Ecological Engineering";
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    void api
      .reportTypes()
      .then((r) => setTypes(r.reportTypes))
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"));
  }, []);

  const { diyIntake, guided } = useMemo(() => {
    let list = types;
    if (regionFilter === "au" || regionFilter === "sg") {
      list = list.filter((t) => regionOf(t.slug) === regionFilter || isPlantSpeciesIdSlug(t.slug));
    }
    const intake = list.filter((t) => isPlantSpeciesIdSlug(t.slug));
    const rest = list.filter((t) => !isPlantSpeciesIdSlug(t.slug));
    if (typeFilter) {
      return {
        diyIntake: intake.filter((t) => t.slug === typeFilter),
        guided: rest.filter((t) => t.slug === typeFilter),
      };
    }
    return { diyIntake: intake, guided: rest };
  }, [types, regionFilter, typeFilter]);

  async function start(rt: ReportType) {
    if (!user) {
      navigate(`/login?next=/reports?type=${encodeURIComponent(rt.slug)}`);
      return;
    }
    setBusyId(rt.id);
    try {
      const { id } = await api.createSubmission({
        reportTypeId: rt.id,
        projectName: "",
      });
      navigate(`/dashboard/reports/${id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="section">
      <div className="container">
        <div className="section-head">
          <div>
            <p className="eyebrow">DIWY · Do it with you</p>
            <h2>Professional reports without the long wait</h2>
            <p>
              Answer the questions, upload files and your logo. We review and release a co-branded
              deliverable, typically in about 48 hours. Catalogue matches Australia environmental
              and Singapore MEP services.
            </p>
          </div>
        </div>
        {error ? <div className="alert alert-error">{error}</div> : null}

        <div className="home-tier-row" style={{ marginBottom: "1.25rem" }}>
          <button
            type="button"
            className={`home-tier-chip ${!regionFilter ? "is-active" : ""}`}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.delete("region");
              setParams(next);
            }}
          >
            All markets
          </button>
          <button
            type="button"
            className={`home-tier-chip ${regionFilter === "au" ? "is-active" : ""}`}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set("region", "au");
              setParams(next);
            }}
          >
            Australia
          </button>
          <button
            type="button"
            className={`home-tier-chip ${regionFilter === "sg" ? "is-active" : ""}`}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set("region", "sg");
              setParams(next);
            }}
          >
            Singapore
          </button>
          {typeFilter ? (
            <button
              type="button"
              className="home-tier-chip"
              onClick={() => {
                const next = new URLSearchParams(params);
                next.delete("type");
                setParams(next);
              }}
            >
              Clear type filter
            </button>
          ) : null}
        </div>

        {diyIntake.length ? (
          <div className="grid-2" style={{ marginBottom: "1.5rem" }}>
            {diyIntake.map((rt) => (
              <article key={rt.id} className="card">
                <div className="short" style={{ color: "var(--orange)", fontWeight: 700 }}>
                  AU · Ecology · DIY photo intake
                </div>
                <h3 style={{ marginTop: "0.35rem" }}>{rt.name}</h3>
                <p>{rt.summary}</p>
                <p className="muted" style={{ fontSize: "0.88rem" }}>
                  {PLANT_SPECIES_ID.photoCopy} {PLANT_SPECIES_ID.fewerCopy}{" "}
                  {PLANT_SPECIES_ID.distinctFrom}
                </p>
                <p className="price" style={{ margin: "0.8rem 0" }}>
                  {formatAud(rt.priceCents)} <small>ex GST</small>
                </p>
                <button
                  className="btn btn-primary btn-sm"
                  type="button"
                  disabled={busyId === rt.id}
                  onClick={() => void start(rt)}
                >
                  {busyId === rt.id ? "Starting…" : "Start Plant species ID"}
                </button>
              </article>
            ))}
          </div>
        ) : null}

        <div className="grid-2">
          {guided.map((rt) => (
            <article key={rt.id} className="card">
              <div className="short" style={{ color: "var(--orange)", fontWeight: 700 }}>
                {regionOf(rt.slug) === "sg" ? "SG · MEP" : "AU · Environment"} · DIWY
              </div>
              <h3 style={{ marginTop: "0.35rem" }}>{rt.name}</h3>
              <p>{rt.summary}</p>
              <p className="price" style={{ margin: "0.8rem 0" }}>
                {formatAud(rt.priceCents)} <small>ex GST · ~{rt.turnaroundHours}h</small>
              </p>
              <button
                className="btn btn-primary btn-sm"
                type="button"
                disabled={busyId === rt.id}
                onClick={() => void start(rt)}
              >
                {busyId === rt.id ? "Starting…" : "Start this report"}
              </button>
            </article>
          ))}
        </div>
        {!diyIntake.length && !guided.length && !error ? (
          <p className="muted">
            {types.length
              ? "No guided reports match this filter."
              : "No guided reports are available right now. Check back shortly, or contact us if you need a report this week."}
          </p>
        ) : null}
        <p className="muted" style={{ marginTop: "1.5rem" }}>
          Only need a template? <Link to="/resources">Browse DIY tools</Link>. Need a cheap plant
          photo ID? <Link to="/resources/plant-species-id">Plant species ID</Link> is A$149.00 incl.
          GST, not the Species Identification Review pack. Truly complex scope?{" "}
          <Link to="/contact">Request consultancy (DIFY)</Link>.
        </p>
      </div>
    </section>
  );
}
